import { memo, useEffect } from 'react'
import { setContext, setTags, setUser } from '@sentry/react'
import Cookies from 'js-cookie'
import { useLocation } from 'react-router-dom'

import { useUserContext } from '../context/UserContext'
import RouteBlocker, { RouteBlockerProps } from './RouteBlocker'

interface PrivateRouteProps extends Omit<RouteBlockerProps, 'shouldRender'> {}

const RequireAuth = ({ redirectTo = '/login', ...props }: PrivateRouteProps) => {
  const user = useUserContext()
  const location = useLocation()

  useEffect(() => {
    if (user) {
      const userContext = {
        id: user.id,
        email: user.email_address!,
        organisation_id: user.organisation_id,
        organisation_name: user.organisation?.name,
        representative_of: user.organisation?.representative_of,
        representative_of_name: user.organisation?.representative_of_org?.name,
      }

      setUser(userContext)
      // custom context content is not indexed by sentry so we need to also set them as tags
      setTags({
        organisation_id: user.organisation_id,
        representative_of: user.organisation?.representative_of,
      })

      // Set user context on the current scope to ensure it's captured
      // on any active transactions, even if they started before user data was available
      setContext('user', userContext)
    }
  }, [location.pathname, user])

  const shouldRender = Boolean(user?.id)

  // If the user is not logged in, save the current location to redirect to after login
  // Only save the location if it is not the root path
  if (!shouldRender && location.pathname !== '/' && location.pathname !== '/login') {
    Cookies.set('login_redirect', location.pathname)
  }

  return <RouteBlocker {...props} redirectTo={redirectTo} shouldRender={shouldRender} />
}

export default memo(RequireAuth)
